import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchInventoryItems,
  fetchInventoryItem,
  updateInventoryItem,
  updateInventoryStatus,
  fetchInventoryMetrics,
  bulkUpdateStatus,
} from '../services/inventoryService';
import { useState, useEffect } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client';
import { GET_RECENT_MOVEMENTS } from '../graphql/queries';

/**
 * @typedef {Object} InventoryItem
 * @property {string} id - The unique identifier of the item
 * @property {string} name - The name of the item
 * @property {string} description - The description of the item
 * @property {string} status - The current status of the item
 * @property {string} [reCommerceFlow] - Optional reCommerce flow status
 */

/**
 * @typedef {Object} InventoryFilter
 * @property {string} [search] - Optional search term
 * @property {string} [status] - Optional status filter
 * @property {string} [sortBy] - Optional sort field
 * @property {string} [sortOrder] - Optional sort order
 */

/**
 * @typedef {Object} InventoryMetrics
 * @property {number} total - Total number of items
 * @property {Object.<string, number>} byStatus - Count of items by status
 */

/**
 * @typedef {Object} BulkUpdateStatusRequest
 * @property {string[]} ids - Array of item IDs to update
 * @property {string} status - New status to set
 * @property {string} [reCommerceFlow] - Optional reCommerce flow to set
 */

const mockInventoryItems = [
  {
    id: 'BILLY001',
    name: 'BILLY Bookcase',
    sku: 'BILLY-80263832',
    category: 'Living Room',
    quantity: 145,
    status: 'In Stock',
    location: 'Main Warehouse',
    lastUpdated: '2024-03-15',
    price: 79.99,
    reorderPoint: 50,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'MALM002',
    name: 'MALM Bed Frame',
    sku: 'MALM-90234567',
    category: 'Bedroom',
    quantity: 32,
    status: 'Low Stock',
    location: 'Main Warehouse',
    lastUpdated: '2024-03-14',
    price: 249.99,
    reorderPoint: 40,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'POANG003',
    name: 'POÄNG Armchair',
    sku: 'POANG-50178923',
    category: 'Living Room',
    quantity: 78,
    status: 'In Stock',
    location: 'Store Front',
    lastUpdated: '2024-03-15',
    price: 129.99,
    reorderPoint: 30,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'KALLAX004',
    name: 'KALLAX Shelf Unit',
    sku: 'KALLAX-20394857',
    category: 'Living Room',
    quantity: 15,
    status: 'Low Stock',
    location: 'Main Warehouse',
    lastUpdated: '2024-03-13',
    price: 89.99,
    reorderPoint: 25,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'LACK005',
    name: 'LACK Side Table',
    sku: 'LACK-30449908',
    category: 'Living Room',
    quantity: 0,
    status: 'Out of Stock',
    location: 'Main Warehouse',
    lastUpdated: '2024-03-12',
    price: 9.99,
    reorderPoint: 100,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'ALEX006',
    name: 'ALEX Drawer Unit',
    sku: 'ALEX-10192837',
    category: 'Office',
    quantity: 42,
    status: 'In Stock',
    location: 'Store Front',
    lastUpdated: '2024-03-15',
    price: 149.99,
    reorderPoint: 35,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'HEMNES007',
    name: 'HEMNES Dresser',
    sku: 'HEMNES-60987654',
    category: 'Bedroom',
    quantity: 5,
    status: 'Low Stock',
    location: 'Main Warehouse',
    lastUpdated: '2024-03-14',
    price: 299.99,
    reorderPoint: 20,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  },
  {
    id: 'IVAR008',
    name: 'IVAR Storage System',
    sku: 'IVAR-40567891',
    category: 'Storage',
    quantity: 89,
    status: 'In Stock',
    location: 'Main Warehouse',
    lastUpdated: '2024-03-15',
    price: 199.99,
    reorderPoint: 45,
    supplier: 'IKEA Manufacturing',
    isVisible: true
  }
];

/**
 * Hook to fetch inventory items with optional filters
 * @param {InventoryFilter} [filters] - Optional filters to apply
 * @returns {import('@tanstack/react-query').UseQueryResult<InventoryItem[]>}
 */
export function useInventoryItems(filters = {}) {
  return useQuery({
    queryKey: ['inventory', filters],
    queryFn: () => {
      // Simulate API delay
      return new Promise((resolve) => {
        setTimeout(() => {
          let filteredItems = [...mockInventoryItems];

          // Apply search filter
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filteredItems = filteredItems.filter(item =>
              item.name.toLowerCase().includes(searchLower) ||
              item.sku.toLowerCase().includes(searchLower)
            );
          }

          // Apply location filter
          if (filters.locationId) {
            filteredItems = filteredItems.filter(item =>
              item.location === filters.locationId
            );
          }

          // Apply status filter
          if (filters.status) {
            filteredItems = filteredItems.filter(item =>
              item.status === filters.status
            );
          }

          resolve(filteredItems);
        }, 500); // 500ms delay to simulate network
      });
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to fetch a single inventory item by ID
 * @param {string} id - The ID of the item to fetch
 * @returns {import('@tanstack/react-query').UseQueryResult<InventoryItem>}
 */
export function useInventoryItem(id) {
  return useQuery({
    queryKey: ['inventory', 'items', id],
    queryFn: () => fetchInventoryItem(id),
  });
}

/**
 * Hook to fetch inventory metrics
 * @returns {import('@tanstack/react-query').UseQueryResult<InventoryMetrics>}
 */
export function useInventoryMetrics() {
  return useQuery({
    queryKey: ['inventory', 'metrics'],
    queryFn: fetchInventoryMetrics,
  });
}

/**
 * Hook to update an inventory item
 * @param {string} id - The ID of the item to update
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useUpdateInventoryItem(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateInventoryItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'metrics'] });
    },
  });
}

/**
 * Hook to update an inventory item's status
 * @param {string} id - The ID of the item to update
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useUpdateInventoryStatus(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ status, reCommerceFlow }) => 
      updateInventoryStatus(id, status, reCommerceFlow),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'metrics'] });
    },
  });
}

/**
 * Hook to bulk update inventory item statuses
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useBulkUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => bulkUpdateStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'metrics'] });
    },
  });
}

/**
 * Hook to fetch recent inventory movements
 * @param {Object} options
 * @param {number} [options.limit=10] - Number of movements to fetch
 * @returns {Object} Recent movements data and loading state
 */
export const useRecentMovements = ({ limit = 10 } = {}) => {
  const { data, loading, error } = useApolloQuery(GET_RECENT_MOVEMENTS, {
    variables: { limit },
    pollInterval: 30000, // Poll every 30 seconds
  });

  const [movements, setMovements] = useState([]);

  useEffect(() => {
    if (data?.recentMovements) {
      setMovements(data.recentMovements);
    }
  }, [data]);

  return {
    movements,
    loading,
    error,
  };
}; 